<script setup lang="ts">
import { useElementVisibility } from '@vueuse/core';
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { browseProducts } from '@/api/algolia';
import { addMixToCart } from '@/api/cart/customizers/mixes';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header3 from '@/components/base/typography/Header3.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import { useCart } from '@/composables/useCart';
import { useCms } from '@/composables/useCms';
import { deriveRelativeRange, VolumetricIngredient } from '@/lib/customizers/ingredients';
import {
  MIX_PRICE_MULTIPLIER,
  MIX_PRODUCT_KEY,
  MIX_VARIANTS,
  MixCustomizer,
} from '@/lib/customizers/mixes';
import { gtag } from '@/utils/analytics';
import { NutsAddToCartItem, sendProductAddedEvent } from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import {
  CustomizerRecommendation,
  CustomizerRecommendationsResponse,
  DataModel,
} from '@/utils/cms';
import { isDefined } from '@/utils/isDefined';

const router = useRouter();
const store = useStore();
const { cart } = useCart(store);

const mixCustomizer = reactive(new MixCustomizer());
const ingredients = ref<VolumetricIngredient[]>([]);
const selections = computed(() => mixCustomizer.selections);
const total = computed(() => mixCustomizer.calculateTotal());

const recommendations = ref<HTMLElement>();
const recommendationsVisible = useElementVisibility(recommendations);
const { content } = useCms<DataModel<CustomizerRecommendationsResponse>>(
  'customizer-recommendations',
  {
    contentVisible: () => recommendationsVisible.value,
    loadOnMount: true,
  },
);

const customMixReportingCategory = 'Snacks > Trail Mix > Custom Mix';
function buildProductAddedPayload(): NutsAddToCartItem {
  return {
    cartId: getCartId(cart.value),
    markedAsGift: false,
    name: 'Custom Trail Mix',
    pageSection: 'Product Recommendations',
    price: total.value,
    priceBeforeDiscount: total.value,
    productId: MIX_PRODUCT_KEY,
    quantity: 1,
    reportingCategory: customMixReportingCategory,
    sku: `${MIX_PRODUCT_KEY}-${MIX_VARIANTS[5].sku}`,
    variant: MIX_VARIANTS[5].en,
    weight: mixCustomizer.weight,
  };
}

const productsLoading = ref(new Set<string>());

const handleAddToCart = async (mix: CustomizerRecommendation) => {
  productsLoading.value.add(mix.code);

  mixCustomizer.prefillSelections(ingredients.value, mix.code as string);

  const payload = buildProductAddedPayload();

  sendProductAddedEvent(payload);

  try {
    await addMixToCart({
      mixName: mix.name,
      quantity: 1,
      selections: selections.value.filter(isDefined),
      weight: mixCustomizer.weight,
    });

    router.push('/cart');
  } catch (error) {
    console.error(error);
    productsLoading.value.delete(mix.code);
  }
};

const handleClickCustomize = (mix: CustomizerRecommendation) => {
  gtag('event', 'customize_existing_mix', {
    mix_name: mix.name,
  });
};

onMounted(async () => {
  const products = await browseProducts({
    filters: 'traits:inMix',
  });

  const relativePriceRange = deriveRelativeRange(products, MIX_PRICE_MULTIPLIER);
  ingredients.value = products
    .map((product) => {
      try {
        const ingredient = VolumetricIngredient.fromAlgoliaVariant(
          product,
          relativePriceRange,
          MIX_PRICE_MULTIPLIER,
        );
        return ingredient;
      } catch {
        return undefined;
      }
    })
    .filter(isDefined);
});
</script>

<template>
  <div v-if="content.result?.data">
    <Header3 headerTag="h2">Recommended Custom Trail Mixes</Header3>
    <div class="flex flex-wrap gap-4 mt-6" ref="recommendations">
      <div
        v-for="entry in content.result.data.entries"
        :key="entry.name"
        class="flex flex-col justify-between flex-1 p-4 text-center bg-white rounded-lg max-w-72 min-w-[14rem]"
      >
        <div>
          <Header5 headerTag="h3">{{ entry.name }}</Header5>
          <img :src="entry.image" :alt="entry.name" class="object-contain my-3 h-44" />
          <BaseBodyText class="px-4">{{ entry.description }}</BaseBodyText>
        </div>
        <div class="flex flex-col gap-2 mt-3">
          <ThemedButton
            class="w-full"
            data-test="add-to-cart-button"
            :isLoading="productsLoading.has(entry.code)"
            @click="handleAddToCart(entry)"
          >
            Add to Cart
          </ThemedButton>
          <RouterLink
            :to="`/custom/${content.result.data.type}/builder?code=${
              entry.code
            }&name=${encodeURIComponent(entry.name)}`"
            class="w-full hover:no-underline"
            @click="handleClickCustomize(entry)"
          >
            <ThemedButton tabindex="-1" theme="white" fullWidth data-test="customize-button"
              >Customize</ThemedButton
            >
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
